/** @flow */

import { takeLatest, put, all, call } from "redux-saga/effects";
import http from "@http";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import type { Action } from "redux-action";
import {
  GetReportingReferralDetailsDefaultReports,
  GetReportingReferralDetailsMyReports,
  GetReportingReferralDetailsReport,
  GetReferralDetailsFiltersSchema,
  CreateReferralDetailsReport,
  UpdateReferralDetailsReport,
  DeleteReferralDetailsReport,
} from "@modules/reporting/constants/actions";
import actionCreators from "@modules/reporting/actionCreator";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";

type ReportRequest = $Exact<{
  clientId: number,
}>;

function* onGetReferralDetailsFiltersSchema({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const schema = yield parseSchema("ReferralDetailsReportFiltersDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetReferralDetailsFiltersSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetReferralDetailsFiltersSchemaError(e.response));
  }
}

function* onGetReportingReferralDetailsDefaultReports({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/referral-details/default-reports`,
    );

    yield put(actionCreators.GetReportingReferralDetailsDefaultReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReportingReferralDetailsDefaultReportsError(e.response));
  }
}

function* onGetReportingReferralDetailsMyReports({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/referral-details/my-saved-reports`,
    );

    yield put(actionCreators.GetReportingReferralDetailsMyReportsSuccess(response.data));
    yield put(actionCreators.ReSetReferralDetailsReportToSave());
  } catch (e) {
    yield put(actionCreators.GetReportingReferralDetailsMyReportsError(e.response));
  }
}

type ReportDataRequest = $Exact<{
  clientId: number,
  report: Object,
  noRedirect?: boolean,
}>;

function* onGetReportingReferralDetailsReport({
  payload,
}: Action<ReportDataRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.report) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const [reportResponse, chartsResponse] = yield all([
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/referral-details`,
        payload.report.filters,
      ),
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/referral-details/charts`,
        payload.report.filters,
      ),
    ]);

    yield all([
      put(actionCreators.GetReportingReferralDetailsReportSuccess(reportResponse.data)),
      put(actionCreators.GetReportingReferralDetailsChartsSuccess(chartsResponse.data)),
    ]);

    if (!payload.noRedirect) {
      yield put(push(route("REPORTING_REFERRAL_DETAILS_REPORT", { clientId: payload.clientId })));
    }
  } catch (e) {
    yield put(actionCreators.GetReportingReferralDetailsReportError(e.response));
  }
}

type CreateReferralDetailsReportRequest = $Exact<{
  clientId: number,
  filters: Object,
  name: string,
}>;

export function* onCreateReferralDetailsReport({
  payload,
}: Action<CreateReferralDetailsReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters || !payload.name) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/reports/client/${payload.clientId}/reports/referral-details/my-saved-reports`,
      {
        name: payload.name,
        filters: payload.filters,
      },
    );

    yield all([
      put(actionCreators.CreateReferralDetailsReportSuccess(response.data)),
      put(actionCreators.GetReportingReferralDetailsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateReferralDetailsReportError(e.response));
  }
}

type UpdateReferralDetailsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  data: Object,
  shouldFetchReportData?: boolean,
}>;

export function* onUpdateReferralDetailsReport({
  payload,
}: Action<UpdateReferralDetailsReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.reportId || !payload.data) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/reports/client/${payload.clientId}/reports/referral-details/my-saved-reports/${payload.reportId}`,
      payload.data,
    );

    const additionalActions = payload.shouldFetchReportData
      ? [
          put(
            actionCreators.GetReportingReferralDetailsReport({
              clientId: payload.clientId,
              report: response.data,
              noRedirect: true,
            }),
          ),
        ]
      : [];

    yield all([
      put(actionCreators.UpdateReferralDetailsReportSuccess(response.data)),
      put(actionCreators.GetReportingReferralDetailsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_UPDATED")),
      ...additionalActions,
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateReferralDetailsReportError(e.response));
  }
}

type DeleteReferralDetailsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  successMessage: string,
}>;

export function* onDeleteReferralDetailsReport({
  payload,
}: Action<DeleteReferralDetailsReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.reportId || !payload.successMessage) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    yield call(
      http.delete,
      `/api/v1/reports/client/${payload.clientId}/reports/referral-details/my-saved-reports/${payload.reportId}`,
    );

    yield all([
      put(actionCreators.DeleteReferralDetailsReportSuccess()),
      put(actionCreators.GetReportingReferralDetailsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteReferralDetailsReportError(e.response));
  }
}

export default function* reportSaga(): Generator<*, void, void> {
  yield all([
    takeLatest(
      GetReportingReferralDetailsDefaultReports,
      onGetReportingReferralDetailsDefaultReports,
    ),
    takeLatest(GetReportingReferralDetailsMyReports, onGetReportingReferralDetailsMyReports),
    takeLatest(GetReportingReferralDetailsReport, onGetReportingReferralDetailsReport),
    takeLatest(GetReferralDetailsFiltersSchema, onGetReferralDetailsFiltersSchema),
    takeLatest(CreateReferralDetailsReport, onCreateReferralDetailsReport),
    takeLatest(UpdateReferralDetailsReport, onUpdateReferralDetailsReport),
    takeLatest(DeleteReferralDetailsReport, onDeleteReferralDetailsReport),
  ]);
}
